import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, User, Menu, X, Home, Package, Users, Phone, HelpCircle, Search, BookOpen, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { CartIcon } from "@/components/cart/CartIcon";
import { Tooltip } from "@/components/onboarding/Tooltip";
import { useOnboarding } from "@/hooks/useOnboarding";
import { RoleBadge } from "@/components/ui/role-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navigationItems = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Products', href: '/products', icon: Package },
  { name: 'Categories', href: '/categories', icon: ShoppingCart },
  { name: 'Sellers', href: '/sellers', icon: Users },
  { name: 'How to Sell', href: '/how-to-use', icon: BookOpen },
  { name: 'Blog', href: '/blog', icon: FileText },
  { name: 'Help', href: '/help', icon: HelpCircle },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  const { isOnboardingComplete } = useOnboarding();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-gradient-glass backdrop-blur-lg">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              SabiTradeNG
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigationItems.slice(1).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActiveRoute(item.href)
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-muted-foreground'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {currentUser?.role === 'seller' && (
              <Link to="/seller/dashboard" className="text-sm font-medium hover:text-primary transition-colors">
                Dashboard
              </Link>
            )}
            {currentUser?.role === 'admin' && (
              <Link to="/admin" className="text-sm font-medium hover:text-primary transition-colors">
                Admin
              </Link>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/track-order')}>
              <Search className="h-4 w-4 mr-2" />
              Track Order
            </Button>
            
            <CartIcon />
            
            {currentUser ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      <div className="flex flex-col gap-1">
                        <span>{currentUser.name || 'User'}</span>
                        {currentUser.role && <RoleBadge role={currentUser.role} />}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(currentUser.role === 'seller' ? '/seller/orders' : '/buyer/orders')}>
                      Orders
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/auth')}>
                  Login
                </Button>
                <Button variant="gradient" onClick={() => navigate('/auth')}>
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center space-x-2">
            <CartIcon />
            {currentUser && <NotificationBell />}
            
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            {/* User Section */}
            <div className="border-b pb-4 mb-4">
              {currentUser ? (
                <>
                  <div className="px-3 py-2 text-sm font-medium text-muted-foreground">
                    <div className="flex flex-col gap-1">
                      <span>{currentUser.name || 'User'}</span>
                      {currentUser.role && <RoleBadge role={currentUser.role} />}
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to={currentUser.role === 'seller' ? '/seller/orders' : '/buyer/orders'}
                    className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Orders
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/auth"
                    className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/auth"
                    className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Navigation Items */}
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActiveRoute(item.href)
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-accent'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="h-4 w-4 mr-3" />
                {item.name}
              </Link>
            ))}
            
            {currentUser?.role === 'seller' && (
              <Link
                to="/seller/dashboard"
                className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Seller Dashboard
              </Link>
            )}
            
            {currentUser?.role === 'admin' && (
              <Link
                to="/admin"
                className="block px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </Link>
            )}

            {/* Additional Mobile Actions */}
            <div className="border-t pt-4 space-y-2">
              <Link
                to="/track-order"
                className="flex items-center px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Search className="h-4 w-4 mr-3" />
                Track Order
              </Link>
              
              <Link
                to="/cart"
                className="flex items-center px-3 py-2 text-sm font-medium hover:bg-accent rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingCart className="h-4 w-4 mr-3" />
                Shopping Cart
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}